import { Link, useOutletContext } from 'react-router-dom';
import { useState } from 'react';

import type { ShellContext } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check } from 'lucide-react';

import { useSettings, useUpdateSettings } from '@/hooks/useAuth';
import { calendarApi, telegram as telegramApi } from '@/lib/api/endpoints';
import { API_PREFIX, ApiError } from '@/lib/api/client';
import { describeFailure } from '@/lib/i18n/errors';
import { useTheme, type Theme } from '@/hooks/useTheme';
import type { Language, TaskDestination } from '@/lib/api/types';
import { LanguagePicker } from '@/components/layout/LanguagePicker';
import { useT, type MessageKey } from '@/lib/i18n';
import { Page } from '@/routes/Today';

export function SettingsRoute() {
  const t = useT();
  const { currentUser } = useOutletContext<ShellContext>();
  const { data: settings, isPending, error, refetch } = useSettings();
  const updateSettings = useUpdateSettings();
  const { theme, setTheme } = useTheme();
  const toast = useToast();

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  if (!settings) return null;

  function save(patch: Parameters<typeof updateSettings.mutate>[0]) {
    updateSettings.mutate(patch, {
      onSuccess: () => toast.show(t('common.saved')),
    });
  }

  return (
    <Page>
      <h1 className="mb-6 text-xl font-semibold tracking-tight">
        {t('settings.title')}
      </h1>

      <Section title={t('settings.account')}>
        <Row label={t('settings.name')}>{currentUser.display_name}</Row>
        <Row label={t('settings.username')}>@{currentUser.username}</Row>
        <Row label={t('settings.email')}>{currentUser.email}</Row>
      </Section>

      <Section title={t('settings.language')} hint={t('settings.language.hint')}>
        {/* The switch is immediate; persisting it to the account is what makes
            it follow the person to the macOS app and to another browser. */}
        <LanguagePicker onChange={(language: Language) => save({ language })} />
      </Section>

      <Section title={t('settings.timezone')} hint={t('settings.timezone.hint')}>
        <TimezoneField
          value={settings.timezone}
          onSave={(timezone) => save({ timezone })}
        />
      </Section>

      <Section title={t('settings.theme')}>
        <div className="flex gap-1.5">
          {(
            [
              ['light', 'settings.theme.light'],
              ['dark', 'settings.theme.dark'],
              ['system', 'settings.theme.system'],
            ] as [Theme, MessageKey][]
          ).map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              variant={theme === value ? 'primary' : 'secondary'}
              onClick={() => setTheme(value)}
            >
              {t(label)}
            </Button>
          ))}
        </div>
      </Section>

      <Section
        title={t('settings.defaultDestination')}
        hint={t('settings.defaultDestination.hint')}
      >
        <div className="flex gap-1.5">
          {(
            [
              ['today', 'move.today'],
              ['backlog', 'move.backlog'],
            ] as [TaskDestination, MessageKey][]
          ).map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              variant={
                settings.default_task_destination === value ? 'primary' : 'secondary'
              }
              onClick={() => save({ default_task_destination: value })}
            >
              {t(label)}
            </Button>
          ))}
        </div>
      </Section>

      <Section title={t('settings.reminders')} hint={t('settings.reminders.hint')}>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.reminder_enabled}
              onChange={(event) => save({ reminder_enabled: event.target.checked })}
              className="h-4 w-4 rounded border-border-strong"
            />
            {t('settings.reminders.enabled')}
          </label>

          <label className="flex items-center gap-2 text-sm">
            <span className="w-28 text-content-muted">
              {t('settings.reminders.interval')}
            </span>
            <select
              value={settings.reminder_interval_minutes}
              onChange={(event) =>
                save({ reminder_interval_minutes: Number(event.target.value) })
              }
              disabled={!settings.reminder_enabled}
              className="h-9 rounded-md border border-border bg-surface px-2 text-sm disabled:opacity-50"
            >
              {[30, 60, 120, 240].map((minutes) => (
                <option key={minutes} value={minutes}>
                  {t('settings.minutes', { count: minutes })}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-28 text-content-muted">{t('settings.onlyBetween')}</span>
            <Input
              type="time"
              value={settings.reminder_start_time.slice(0, 5)}
              onChange={(event) =>
                save({ reminder_start_time: `${event.target.value}:00` })
              }
              disabled={!settings.reminder_enabled}
              className="w-28"
              aria-label={t('settings.reminders.start')}
            />
            <span className="text-content-subtle">{t('settings.and')}</span>
            <Input
              type="time"
              value={settings.reminder_end_time.slice(0, 5)}
              onChange={(event) =>
                save({ reminder_end_time: `${event.target.value}:00` })
              }
              disabled={!settings.reminder_enabled}
              className="w-28"
              aria-label={t('settings.reminders.end')}
            />
          </div>
        </div>
      </Section>

      <CalendarSection />

      <TelegramSection />

      <TelegramCaptureSection />

      <Section title={t('settings.shortcuts')}>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          {(
            [
              ['N', 'settings.shortcut.newTask'],
              ['Q', 'settings.shortcut.newTask'],
              ['⌘K', 'settings.shortcut.search'],
              ['G', 'settings.shortcut.today'],
              ['Esc', 'common.closeDialog'],
            ] as [string, MessageKey][]
          ).map(([key, description]) => (
            <div key={key} className="contents">
              <dt>
                <kbd className="rounded border border-border px-1.5 py-0.5 text-xs">
                  {key}
                </kbd>
              </dt>
              <dd className="text-content-muted">{t(description)}</dd>
            </div>
          ))}
        </dl>
      </Section>
    </Page>
  );
}

/**
 * Telegram: praise on completion, and capture-by-forward.
 *
 * Hidden entirely when the server has no bot configured — /me/telegram 404s
 * and the query stays in error state. A disabled section would only raise a
 * question the user cannot act on.
 */
/**
 * Connecting a Google Calendar.
 *
 * A separate consent from signing in, on purpose: asking every new user for
 * calendar access at the door would put a scarier screen in front of people who
 * will never use this. Read-only — MirOnDo never writes to anyone's calendar,
 * and the copy says so, because "connect your calendar" is a sentence people
 * are right to be careful about.
 */
function CalendarSection() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['calendar', 'status'],
    queryFn: () => calendarApi.status(),
    retry: false,
    staleTime: 30_000,
  });

  const disconnect = useMutation({
    mutationFn: () => calendarApi.disconnect(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (error) => toast.error(describeFailure(error, 'common.error')),
  });

  if (!data || (!data.available && !data.feeds_available)) return null;

  return (
    <Section title={t('calendar.title')} hint={t('calendar.hint')}>
      {!data.available ? null : data.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5 text-sm text-accent">
            <Check className="h-4 w-4" aria-hidden />
            {t('calendar.connected', { email: data.account_email ?? '' })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={disconnect.isPending}
            onClick={() => disconnect.mutate()}
          >
            {t('calendar.disconnect')}
          </Button>
          {data.problem ? <CalendarProblem status={data} /> : null}
        </div>
      ) : (
        // A full page navigation, not fetch: this ends at Google's consent
        // screen, which cannot be reached from inside a request.
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            window.location.href = `${API_PREFIX}/me/calendar/connect`;
          }}
        >
          {t('calendar.connect')}
        </Button>
      )}
      {data.feeds_available ? <CalendarFeeds feeds={data.feeds} /> : null}
    </Section>
  );
}

/**
 * Subscribed ICS feeds — the calendars that are not Google's.
 *
 * Outlook, iCloud, Nextcloud and most company systems can publish a calendar
 * as a secret ICS address; MirOnDo reads a day from it. The address never
 * comes back from the server (it is a credential), so a feed is shown only by
 * the name it gave itself.
 */
function CalendarFeeds({
  feeds,
}: {
  feeds: {
    id: string;
    name: string;
    problem: string | null;
    problem_detail: string | null;
  }[];
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['calendar'] });

  const add = useMutation({
    mutationFn: () => calendarApi.addFeed(url.trim()),
    onSuccess: () => {
      setUrl('');
      refresh();
    },
    onError: (error) => toast.error(describeFailure(error, 'calendar.feeds.addFailed')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => calendarApi.removeFeed(id),
    onSuccess: refresh,
    onError: (error) => toast.error(describeFailure(error, 'common.error')),
  });

  return (
    <div className="mt-4 border-t border-border pt-4">
      <h3 className="text-xs font-semibold text-content">{t('calendar.feeds.title')}</h3>
      <p className="mb-3 mt-0.5 text-xs text-content-subtle">{t('calendar.feeds.hint')}</p>

      {feeds.length > 0 ? (
        <ul className="mb-3 space-y-2">
          {feeds.map((feed) => (
            <li key={feed.id} className="text-sm">
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 text-content">
                  <Check className="h-4 w-4 text-accent" aria-hidden />
                  {feed.name}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(feed.id)}
                >
                  {t('calendar.feeds.remove')}
                </Button>
              </div>
              {feed.problem ? <CalendarProblem status={feed} /> : null}
            </li>
          ))}
        </ul>
      ) : null}

      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (url.trim()) add.mutate();
        }}
      >
        <Input
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder={t('calendar.feeds.placeholder')}
          className="min-w-0 flex-1"
          autoComplete="off"
        />
        <Button type="submit" variant="secondary" size="sm" disabled={!url.trim() || add.isPending}>
          {add.isPending ? t('calendar.feeds.checking') : t('calendar.feeds.add')}
        </Button>
      </form>
    </div>
  );
}

function TelegramSection() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isError } = useQuery({
    queryKey: ['telegram'],
    queryFn: () => telegramApi.status(),
    retry: false,
    staleTime: 60_000,
  });

  const connect = useMutation({
    mutationFn: () => telegramApi.link(),
    onSuccess: ({ url }) => {
      // A t.me link has to open in a real tab: the Telegram app claims it.
      window.open(url, '_blank', 'noopener');
      // The link completes out-of-band (in Telegram), so poll the status a
      // few times rather than asking the user to refresh.
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        void queryClient.invalidateQueries({ queryKey: ['telegram'] });
        if (attempts >= 10) window.clearInterval(timer);
      }, 3000);
    },
  });

  const disconnect = useMutation({
    mutationFn: () => telegramApi.unlink(),
    onSuccess: () => {
      toast.show(t('common.saved'));
      void queryClient.invalidateQueries({ queryKey: ['telegram'] });
    },
  });

  if (isError || !data) return null;

  return (
    <Section title="Telegram" hint={t('telegram.hint')}>
      {data.linked ? (
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-sm text-accent">
            <Check className="h-4 w-4" aria-hidden />
            {t('telegram.linked', { bot: `@${data.bot_username}` })}
          </span>
          <Button variant="secondary" size="sm" onClick={() => disconnect.mutate()}>
            {t('telegram.unlink')}
          </Button>
        </div>
      ) : (
        <Button
          variant="primary"
          size="sm"
          onClick={() => connect.mutate()}
          disabled={connect.isPending}
        >
          {t('telegram.connect', { bot: `@${data.bot_username}` })}
        </Button>
      )}
      <Link
        to="/guide"
        className="mt-3 inline-block text-xs text-accent underline-offset-2 hover:underline"
      >
        {t('telegram.guideLink')} →
      </Link>
    </Section>
  );
}

/**
 * Mention capture — the user's own Telegram account.
 *
 * The heavy half of the integration: the account's login session lives on the
 * server, encrypted, and a worker watches for @mentions in groups. The flow is
 * Telegram's own: phone → code from the app → optionally the 2FA password.
 * Hidden entirely when the server lacks the MTProto pair or the vault key.
 */
function TelegramCaptureSection() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);

  const { data } = useQuery({
    queryKey: ['telegram', 'userbot'],
    queryFn: () => telegramApi.userbotStatus(),
    retry: false,
    staleTime: 30_000,
  });

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['telegram', 'userbot'] });

  const start = useMutation({
    mutationFn: () => telegramApi.userbotStart(phone),
    onSuccess: refresh,
    onError: (error) => toast.error(describeFailure(error, 'common.error')),
  });
  // A code that never arrives leaves the form asking for one, with no way out.
  // Sending again needs no phone — the server still holds the number.
  const resend = useMutation({
    mutationFn: () => telegramApi.userbotStart(),
    onSuccess: () => {
      toast.show(t('tgCapture.codeResent'));
      refresh();
    },
    onError: (error) => toast.error(describeFailure(error, 'common.error')),
  });
  const confirm = useMutation({
    mutationFn: () => telegramApi.userbotConfirm(code, password || undefined),
    onSuccess: () => {
      toast.show(t('common.saved'));
      setCode('');
      setPassword('');
      setNeedsPassword(false);
      refresh();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'TG_PASSWORD_NEEDED') {
        setNeedsPassword(true);
        return;
      }
      toast.error(describeFailure(error, 'common.error'));
    },
  });
  const disconnect = useMutation({
    mutationFn: () => telegramApi.userbotDisconnect(),
    onSuccess: refresh,
  });

  if (!data?.available) return null;

  return (
    <Section title={t('tgCapture.title')} hint={t('tgCapture.hint')}>
      {data.connected ? (
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-sm text-accent">
            <Check className="h-4 w-4" aria-hidden />
            {t('tgCapture.connected', { phone: data.phone ?? '' })}
          </span>
          <Button variant="secondary" size="sm" onClick={() => disconnect.mutate()}>
            {t('telegram.unlink')}
          </Button>
        </div>
      ) : data.pending ? (
        <form
          className="flex max-w-md flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (code.trim()) confirm.mutate();
          }}
        >
          {/* Which message depends on where Telegram actually put the code.
              The generic one said "sent a code to <number>", which reads as a
              text message — and Telegram sends it inside the app whenever it
              can, so people wait for an SMS that is never coming. */}
          <p className="text-xs text-content-muted">
            {t(
              data.delivery === 'sms'
                ? 'tgCapture.codeSent.sms'
                : data.delivery === 'call'
                  ? 'tgCapture.codeSent.call'
                  : 'tgCapture.codeSent.app',
              { phone: data.phone ?? '' },
            )}
          </p>
          <div className="flex gap-2">
            <Input
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder={t('tgCapture.code')}
              className="w-36"
              autoComplete="one-time-code"
            />
            {needsPassword ? (
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t('tgCapture.password')}
                className="flex-1"
              />
            ) : null}
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!code.trim() || confirm.isPending}
            >
              {t('common.continue')}
            </Button>
          </div>
          {needsPassword ? (
            <p className="text-xs text-overdue">{t('tgCapture.passwordHint')}</p>
          ) : null}

          {/* The way out of a login that stalled. Without these the only
              escape from "enter the code" was a code that never came. */}
          <div className="flex gap-3 text-xs">
            <button
              type="button"
              onClick={() => resend.mutate()}
              disabled={resend.isPending}
              className="text-accent underline-offset-2 hover:underline disabled:opacity-50"
            >
              {t('tgCapture.resend')}
            </button>
            <button
              type="button"
              onClick={() => {
                setCode('');
                setPassword('');
                setNeedsPassword(false);
                disconnect.mutate();
              }}
              disabled={disconnect.isPending}
              className="text-content-muted underline-offset-2 hover:underline disabled:opacity-50"
            >
              {t('tgCapture.changeNumber')}
            </button>
          </div>
        </form>
      ) : (
        <form
          className="flex max-w-md gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (phone.trim()) start.mutate();
          }}
        >
          <Input
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+420 777 123 456"
            className="w-48"
          />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!phone.trim() || start.isPending}
          >
            {t('tgCapture.connect')}
          </Button>
        </form>
      )}
    </Section>
  );
}

/**
 * Every reason a connected calendar can still show nothing.
 *
 * Spelled out rather than derived from the code at runtime: the catalogue is
 * key-checked at compile time, so a server-side code with no message here is a
 * build error rather than a banner reading `calendar.problem.SCOPE_MISSING`.
 */
const CALENDAR_PROBLEMS = {
  API_NOT_ENABLED: 'calendar.problem.API_NOT_ENABLED',
  SCOPE_MISSING: 'calendar.problem.SCOPE_MISSING',
  ACCESS_REVOKED: 'calendar.problem.ACCESS_REVOKED',
  REFRESH_REJECTED: 'calendar.problem.REFRESH_REJECTED',
  VAULT_KEY_CHANGED: 'calendar.problem.VAULT_KEY_CHANGED',
  CALENDAR_NOT_FOUND: 'calendar.problem.CALENDAR_NOT_FOUND',
  GOOGLE_UNREACHABLE: 'calendar.problem.GOOGLE_UNREACHABLE',
  FEED_UNREACHABLE: 'calendar.problem.FEED_UNREACHABLE',
  FEED_REDIRECTED: 'calendar.problem.FEED_REDIRECTED',
  FETCH_FAILED: 'calendar.problem.FETCH_FAILED',
} as const satisfies Record<string, MessageKey>;

/**
 * Why a connected calendar shows nothing.
 *
 * Worth a banner rather than a silent empty strip: "connected" and "showing
 * your day" are different claims, and the gap between them is where every
 * report of "it does not work" comes from. Google's own sentence goes
 * underneath — for the commonest cause, the Calendar API never enabled on the
 * project, that sentence contains the console link that fixes it.
 */
function CalendarProblem({
  status,
}: {
  status: { problem: string | null; problem_detail: string | null };
}) {
  const t = useT();
  const key =
    CALENDAR_PROBLEMS[status.problem as keyof typeof CALENDAR_PROBLEMS] ??
    CALENDAR_PROBLEMS.FETCH_FAILED;

  return (
    <p
      role="status"
      className="w-full rounded-lg bg-danger-subtle px-3 py-2 text-xs leading-relaxed text-danger"
    >
      <span className="flex items-start gap-1.5 font-medium">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        {t(key)}
      </span>
      {status.problem_detail ? (
        <span className="mt-1 block break-words opacity-80">{status.problem_detail}</span>
      ) : null}
    </p>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-7 border-b border-border pb-7 last:border-0">
      <h2 className="mb-1 text-sm font-semibold">{title}</h2>
      {hint ? <p className="mb-3 text-xs text-content-subtle">{hint}</p> : null}
      <div className={hint ? '' : 'mt-3'}>{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-4 py-1 text-sm">
      <span className="w-40 shrink-0 text-content-subtle">{label}</span>
      <span className="text-content">{children}</span>
    </div>
  );
}

/**
 * Timezone picker.
 *
 * `Intl.supportedValuesOf` gives the full IANA list from the browser, so there
 * is no hard-coded table here to fall out of date.
 */
function TimezoneField({
  value,
  onSave,
}: {
  value: string;
  onSave: (timezone: string) => void;
}) {
  const t = useT();
  const [zones] = useState<string[]>(() => {
    try {
      return Intl.supportedValuesOf('timeZone');
    } catch {
      return [value, 'Europe/Prague', 'UTC'];
    }
  });

  return (
    <select
      value={value}
      onChange={(event) => onSave(event.target.value)}
      className="h-9 w-full max-w-xs rounded-md border border-border bg-surface px-2 text-sm"
      aria-label={t('settings.timezone')}
    >
      {zones.map((zone) => (
        <option key={zone} value={zone}>
          {zone}
        </option>
      ))}
    </select>
  );
}
